/** Today's numbers for the 5/50 EMA trend rule — a pre-market reference.
 *
 * The rule's thresholds all move with the session's volatility: the ATR band
 * decides how a trade gets on, and State 2 decides when the 5 EMA takes over
 * as the exit. Both are worth knowing BEFORE a signal fires rather than after,
 * which is the whole reason this sits on the dashboard.
 *
 * ⚠️ Every figure is in INDEX POINTS on the underlying, never option premium.
 * A 40-point index stop is not a 40-rupee premium stop, and the card says so
 * rather than letting the number be read either way.
 *
 * ⚠️ A missing figure stays missing. These size real trades, so a failed
 * broker read shows a dash and its reason — never a stale value, and never a
 * zero, which would read as a valid tight stop.
 */

import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { emaTrendApi, type EmaTrendParameters } from "@/lib/emaTrendApi";

const KEY: readonly unknown[] = ["ema-trend-parameters"];
/** Slow on purpose: VIX and ATR move on 15-min closes, not on ticks. */
const POLL_MS = 60_000;

/** A 200 carrying the wrong SHAPE is not an error react-query can see, so the
 * payload is checked once here. This card is a reference and its host page
 * carries the open position — it must not take the dashboard down. */
function isParameters(value: EmaTrendParameters | undefined): value is EmaTrendParameters {
  return value !== undefined && "underlying" in value && Array.isArray(value.unavailable);
}

function points(value: number | null, dp = 1): string {
  return value === null ? "—" : value.toFixed(dp);
}

function Figure({
  label,
  value,
  hint,
  strong,
}: Readonly<{ label: string; value: string; hint?: string; strong?: boolean }>): ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-wider text-ink-muted">{label}</span>
      <span
        className={`font-mono tabular-nums ${strong ? "text-base font-medium text-accent" : "text-sm text-ink"}`}
      >
        {value}
      </span>
      {hint !== undefined && <span className="text-[10px] text-ink-muted">{hint}</span>}
    </div>
  );
}

export function EmaTrendParametersCard(): ReactElement {
  const q = useQuery({
    queryKey: KEY,
    queryFn: () => emaTrendApi.parameters(),
    refetchInterval: POLL_MS,
  });
  return (
    <section className="card">
      <div className="mb-3 flex items-center gap-2">
        <i className="ti ti-chart-candle text-lg text-accent" />
        <h3 className="inline">5/50 EMA — today&apos;s numbers</h3>
      </div>
      <Body data={q.data} loading={q.isPending} failed={q.isError} />
    </section>
  );
}

function Identity({ data }: Readonly<{ data: EmaTrendParameters }>): ReactElement {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="rounded-ctl bg-surface-3 px-2 py-1 font-mono text-xs text-accent">
        {data.underlying}
      </span>
      <span className="rounded-ctl bg-surface-3 px-2 py-1 font-mono text-xs text-ink-secondary">
        ITM-{data.itm_depth} · {points(data.itm_points, 0)} pts in
      </span>
      <span className="text-[11px] text-ink-muted">
        {data.fast_ema}/{data.slow_ema} EMA · ATR({data.atr_period}) · 15-min
      </span>
    </div>
  );
}

function Readings({ data }: Readonly<{ data: EmaTrendParameters }>): ReactElement {
  return (
    <div className="grid grid-cols-2 gap-3 rounded-ctl bg-surface-2 p-3 sm:grid-cols-4">
      <Figure label="Spot" value={points(data.spot, 2)} />
      <Figure label="India VIX" value={points(data.vix, 2)} />
      <Figure label="ATR(14)" value={points(data.atr)} hint="15-min" />
      <Figure
        label="Smallest candle it will take"
        value={points(data.min_candle_points)}
        hint="anything smaller is refused"
        strong
      />
    </div>
  );
}

/** Percentages come from the API rather than being divided here, so the card
 * cannot disagree with the engine when a figure is missing. */
function withPct(pts: number | null, pct: number | null): string {
  if (pts === null) return "—";
  return pct === null ? points(pts) : `${points(pts)}  (${pct.toFixed(2)}%)`;
}

/** The LEVEL a distance resolves to, beside what it is measured from — the
 * operator reads a chart in prices, not in offsets (2026-09-07). Falls back to
 * the plain hint when a level could not be derived. */
function levels(
  from: string,
  at: number | null,
  up: number | null,
  down: number | null,
  fallback: string,
): string {
  if (at === null || up === null || down === null) return fallback;
  return `${from} ${points(at, 2)} → above ${points(up, 2)} · below ${points(down, 2)}`;
}

/** One row per CASE, phrased as the question the operator asks at the chart.
 * These are the same numbers the old "Risk band / State 2 / State 3" row
 * carried — renamed because those were mechanism names, not decisions. */
function Cases({ data }: Readonly<{ data: EmaTrendParameters }>): ReactElement {
  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-3 rounded-ctl bg-surface-2 p-3 sm:grid-cols-2">
        <Figure
          label="Case 1 · 5 EMA takes over the stop after"
          value={withPct(data.state2_points, data.state2_pct)}
          hint={levels(
            "from spot",
            data.spot,
            data.state2_level_long,
            data.state2_level_short,
            "until then the fixed stop is live",
          )}
          strong
        />
        <Figure
          label="Case 1 · stop must be at least"
          value={points(data.min_stop_points)}
          hint={`widened below ${points(data.floor_points)}, rests a limit above ${points(data.cap_points)}`}
        />
      </div>
      <div className="grid grid-cols-1 gap-3 rounded-ctl bg-surface-2 p-3 sm:grid-cols-2">
        <Figure
          label="Case 2 · reversal needs price this far from the 50 EMA"
          value={withPct(data.reversal_points, data.reversal_pct)}
          hint={levels(
            "from 50 EMA",
            data.slow_ema_value,
            data.reversal_level_above,
            data.reversal_level_below,
            "scales with VIX — moves every session",
          )}
          strong
        />
        <Figure
          label="Case 2 · candle must be at least"
          value={points(data.min_candle_points)}
          hint="and sit wholly clear of the 5 EMA"
        />
      </div>
    </div>
  );
}

export function Body({
  data,
  loading,
  failed,
}: Readonly<{
  data: EmaTrendParameters | undefined;
  loading: boolean;
  failed: boolean;
}>): ReactElement {
  if (loading) return <p className="text-xs text-ink-muted">Reading today&apos;s levels…</p>;
  if (failed || !isParameters(data)) {
    return <p className="text-xs text-ink-muted">Could not read today&apos;s levels.</p>;
  }
  if (data.underlying === null) {
    return (
      <p className="text-xs text-ink-muted">
        No session today — the rule trades NIFTY on Monday, Tuesday and Friday, and SENSEX on
        Wednesday and Thursday.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <Identity data={data} />
      <Readings data={data} />
      <Cases data={data} />
      <p className="text-[10px] text-ink-muted">
        All figures are index points on {data.underlying}, not option premium.
      </p>
      {data.unavailable.length > 0 && (
        <ul className="flex flex-col gap-1 rounded-ctl bg-warning/10 p-2 text-[11px] text-warning">
          {data.unavailable.map((reason) => (
            <li key={reason}>{reason}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
